import { useEffect, useState } from 'react'

import { ReactComponent as ChervronIcon } from 'assets/images/icons/chevron-right.svg'
import { Icon } from 'components/uikit/Icon/Icon'
import { AnalyticsActionType } from 'types/analytics.type'
import { trackAnalytics } from 'utils/analytics'

import styles from './Pagination.module.scss'

interface PaginationProps {
  totalItems: number
  pageSize: number
  currentPage: number
  className?: string
  onPageChanged?: (page: number) => void
  itemsPerPageChanged?: (itemsPerPage: number) => void
}

export const Pagination = (props: PaginationProps) => {
  const { totalItems, pageSize, currentPage, onPageChanged, className, itemsPerPageChanged } = props

  const [currentPageState, setCurrentPageState] = useState(currentPage)
  const [itemsPerPage, setItemsPerPage] = useState(pageSize)

  const disabled = totalItems <= itemsPerPage

  useEffect(() => {
    setCurrentPageState(currentPage)
  }, [currentPage])

  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = currentPageState * itemsPerPage + 1
  const endIndex = Math.min(startIndex + itemsPerPage - 1, totalItems)
  const isFirstPage = currentPageState === 0
  const isLastPage = currentPageState === totalPages - 1

  const previousPage = () => {
    if (disabled) return
    if (currentPageState > 0) {
      setCurrentPageState(currentPageState - 1)
      onPageChanged && onPageChanged(currentPageState - 1)
    }
    trackAnalytics({
      type: AnalyticsActionType.action,
      payload: {
        action: 'pg_act_toolsandsupport_allworktools_pagechange',
        params: [
          {
            key: 'currentpage',
            value: currentPageState.toString(),
          },
        ],
      },
    })
  }

  const nextPage = () => {
    if (disabled) return
    if (currentPageState < totalPages - 1) {
      setCurrentPageState(currentPageState + 1)
      onPageChanged && onPageChanged(currentPageState + 1)
    }
    trackAnalytics({
      type: AnalyticsActionType.action,
      payload: {
        action: 'pg_act_toolsandsupport_allworktools_pagechange',
        params: [
          {
            key: 'currentpage',
            value: (currentPageState + 2).toString(),
          },
        ],
      },
    })
  }

  const gotoPage = (page: number) => {
    if (disabled) return
    if (page > 0 && page <= totalPages) {
      setCurrentPageState(page - 1)
      onPageChanged && onPageChanged(page - 1)
    }
    trackAnalytics({
      type: AnalyticsActionType.action,
      payload: {
        action: 'pg_act_toolsandsupport_allworktools_pagechange',
        params: [
          {
            key: 'currentpage',
            value: (page + 1).toString(),
          },
        ],
      },
    })
  }

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    itemsPerPageChanged && itemsPerPageChanged(newItemsPerPage)

    const newTotalPages = Math.ceil(totalItems / newItemsPerPage)
    const newPage = Math.min(currentPageState, newTotalPages - 1)

    setCurrentPageState(newPage)
    onPageChanged && onPageChanged(newPage)
    //  pg_act_toolsandsupport_allworktools_itemsperpage
    trackAnalytics({
      type: AnalyticsActionType.action,
      payload: {
        action: 'pg_act_toolsandsupport_allworktools_itemsperpage',
        params: [
          {
            key: 'itemsperpage',
            value: newItemsPerPage.toString(),
          },
        ],
      },
    })
  }

  return (
    <div className={`flex justify-between items-center ${className}`}>
      <div className="flex items-center font-medium">
        <span className="mr-[8px]">Items per page:</span>
        <select className="bg-transparent" onChange={ev => handleItemsPerPageChange(parseInt(ev.target.value))}>
          {Array.from({ length: 6 }).map((_, i) => {
            const value = (i + 1) * 10
            return <option key={value}>{value}</option>
          })}
        </select>
        <div className="h-[12px] ml-[6px] mr-[16px] border-solid border-[1px] border-[#BDBDBD]" />
        <div>
          <span>
            {startIndex}-{endIndex} of {totalItems} items
          </span>
        </div>
      </div>
      <div className="flex items-center">
        <Icon
          icon={ChervronIcon}
          color="#8B919A"
          onClick={previousPage}
          className={`rotate-180  ${isFirstPage ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
        />
        <input
          className={`${styles.input} text-center rounded-[6px] border-solid border-[1px] w-[40px] border-[#8B8B8B] px-[10px] py-[5px] items-center flex justify-center`}
          type="number"
          min={1}
          max={totalPages}
          value={currentPageState + 1}
          disabled={disabled}
          onChange={ev => gotoPage(parseInt(ev.target.value))}
        />
        <div className="h-[12px] ml-[6px] mr-[16px] border-solid border-[1px] border-[#BDBDBD]" />
        <span>{totalPages}</span>
        <Icon
          icon={ChervronIcon}
          className={`${disabled ? 'cursor-not-allowed' : isLastPage ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'} `}
          color="#8B919A"
          onClick={nextPage}
        />
      </div>
    </div>
  )
}
